import { assignValuesFromRouteQuery, pushValuesToRouteQuery, scrollIntoView } from '@/utils';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import ArrowRightLight from '@/assets/arrowRightLight.svg';
import tailwindConfig from '@/tailwind.screens';
import { MetadataContext } from '../ContextProviders';

type PaginatorType = {
  pageSize: number;
  totalResults: number;
  goToElement?: string;
  queryMode?: boolean;
  className?: string;
  value?: number;
  onChange?: (data: { page: number }) => void;
};

const Paginator = (data: PaginatorType) => {
  const { pageSize, totalResults, className, goToElement, queryMode, value, onChange } = data;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVisible, setTotalVisible] = useState(7);
  const [btns, setBtns] = useState<{ label: string; value: number }[]>([]);
  const totalPages = useRef(1);
  const router = useRouter();
  const t = useTranslations();
  const { isPublic } = useContext(MetadataContext);

  useEffect(() => {
    const pageObj = { page: 1 };
    assignValuesFromRouteQuery(router, pageObj);
    setCurrentPage(Number(pageObj.page || 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query]);

  useEffect(() => {
    setCurrentPage(Number(value || 1));
  }, [value]);

  useEffect(() => {
    totalPages.current = Math.ceil(totalResults / pageSize);

    const maxVisible = Math.max(totalVisible || 8, 5); // minimum 5
    const N = totalPages.current;

    const newBtns: { label: string; value: number }[] = [];

    if (N <= maxVisible) {
      for (let p = 1; p <= N; p++) {
        newBtns.push({ label: String(p), value: p });
      }
      setBtns(newBtns);
      return;
    }

    const first = 1;
    const last = N;

    // Adjust ellipsis thresholds depending on visible limit
    const leftThreshold = Math.ceil(maxVisible / 2);
    const rightThreshold = N - leftThreshold + 1;

    const needLeft = currentPage > leftThreshold;
    const needRight = currentPage < rightThreshold;

    // Remaining slots for middle pages after first/last and ellipses
    let remaining = maxVisible - 2; // minus first & last
    if (needLeft) remaining -= 1; // reserve left ellipsis
    if (needRight) remaining -= 1; // reserve right ellipsis

    // Compute middle window
    let start: number;
    let end: number;

    if (!needLeft && needRight) {
      // Near start
      start = 2;
      end = start + remaining - 1;
    } else if (needLeft && !needRight) {
      // Near end
      end = last - 1;
      start = end - remaining + 1;
    } else {
      // Middle
      const left = Math.floor(remaining / 2);
      const right = remaining - left - 1;
      start = currentPage - left;
      end = currentPage + right;

      if (start < 2) {
        end += 2 - start;
        start = 2;
      }
      if (end > last - 1) {
        start -= end - (last - 1);
        end = last - 1;
      }
    }

    // Build buttons
    newBtns.push({ label: String(first), value: first });
    if (needLeft) newBtns.push({ label: '...', value: start - 1 });
    for (let p = start; p <= end; p++) newBtns.push({ label: String(p), value: p });
    if (needRight) newBtns.push({ label: '...', value: end + 1 });
    newBtns.push({ label: String(last), value: last });

    setBtns(newBtns);
  }, [pageSize, totalResults, currentPage, totalVisible]);

  useEffect(() => {
    const onResize = () => {
      setTotalVisible(window.innerWidth >= parseInt(tailwindConfig.screens.md, 10) ? 7 : 5);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const setPage = (newPage: number) => {
    if (queryMode) {
      pushValuesToRouteQuery(router, {
        page: newPage > 1 ? newPage : null,
      });
    } else {
      setCurrentPage(newPage);
    }
    if (onChange) {
      onChange({
        page: newPage,
      });
    }
    if (goToElement) {
      const element = document.querySelector(goToElement) as HTMLElement | null;
      if (element) {
        const style = window.getComputedStyle(document.body);
        if (window.lenis) {
          window.lenis.scrollTo(element, { offset: parseInt(style.getPropertyValue('--header-height'), 10) * -1 });
        } else {
          scrollIntoView(element, parseInt(style.getPropertyValue('--header-height'), 10) * -1);
        }
      }
    }
  };

  if (totalPages.current <= 1) return;

  return isPublic ? (
    <div className={classNames('pagination mt-3 flex items-center justify-center md:mt-12', className)}>
      <button
        className="btn icon btn-from-bg mx-3"
        disabled={currentPage <= 1}
        onClick={() => setPage(currentPage - 1)}
        aria-label={t('general.$previous')}
      >
        <ArrowRightLight className="!h-8 !w-8 -rotate-180" />
      </button>

      {btns.map((btn) => (
        <button
          className={classNames(
            'btn icon btn-from-bg mx-3 !rounded-none border-b-2',
            currentPage === btn.value ? 'border-b-black' : 'border-b-transparent',
          )}
          key={btn.value}
          disabled={btn.label === '...'}
          onClick={() => setPage(btn.value)}
        >
          {btn.label} {currentPage === btn.value}
        </button>
      ))}

      <button
        className="btn icon btn-from-bg mx-3"
        disabled={currentPage >= totalPages.current}
        onClick={() => setPage(currentPage + 1)}
        aria-label={t('general.$next')}
      >
        <ArrowRightLight />
      </button>
    </div>
  ) : (
    <div className={classNames('pagination mt-3 flex items-center justify-center md:mt-12', className)}>
      <button
        className="btn icon btn-from-bg mx-3"
        disabled={currentPage <= 1}
        onClick={() => setPage(currentPage - 1)}
        aria-label={t('general.$previous')}
      >
        <ArrowRightLight className="!h-6 !w-6 -rotate-180" />
      </button>

      {btns.map((btn) => (
        <button
          className={classNames(
            'btn icon btn-from-bg mx-2 !rounded-none border-b-1 !pb-1 !text-lg !font-normal',
            currentPage === btn.value ? 'border-b-black' : 'border-b-transparent',
          )}
          key={btn.value}
          disabled={btn.label === '...'}
          onClick={() => setPage(btn.value)}
        >
          {btn.label} {currentPage === btn.value}
        </button>
      ))}

      <button
        className="btn icon btn-from-bg mx-3"
        disabled={currentPage >= totalPages.current}
        onClick={() => setPage(currentPage + 1)}
        aria-label={t('general.$next')}
      >
        <ArrowRightLight className="!h-6 !w-6" />
      </button>
    </div>
  );
};

export default Paginator;
